/* Loupe – collection grid: column switcher, load more / infinite scroll. Vanilla, no dependencies.
   Works alongside loupe-facets (theme.js): everything inside [data-facets-swap="grid"] is replaced on each
   filter/sort refresh, so <loupe-collection-grid> simply starts fresh whenever the facets form re-renders it. */
(() => {
  const KEY = { d: 'loupe:collection-cols-d', m: 'loupe:collection-cols-m' };
  const read = (k) => { try { return localStorage.getItem(k); } catch (e) { return null; } };
  const write = (k, v) => { try { localStorage.setItem(k, v); } catch (e) {} };
  const tablet = (d) => Math.min(+d || 3, 3);

  /* image sizes follow the chosen columns so 2-up grids stay sharp */
  const applySizes = (root, scope) => {
    if (!root) return;
    const d = root.dataset.colsD || 4, m = root.dataset.colsM || 2;
    const sizes = `(min-width: 990px) calc(100vw / ${d}), (min-width: 750px) calc(100vw / ${tablet(d)}), calc(100vw / ${m})`;
    (scope || root).querySelectorAll('.mc-grid .card__media img').forEach((img) => { if (img.sizes !== sizes) img.sizes = sizes; });
  };

  /* ---------- column switcher (radio groups, remembered per browser) ---------- */
  class LoupeColumns extends HTMLElement {
    connectedCallback() {
      this.root = this.closest('.mc__root');
      if (!this.root) return;
      ['d', 'm'].forEach((g) => {
        const allowed = (this.dataset['options' + g.toUpperCase()] || '').split(',');
        const saved = read(KEY[g]);
        if (saved && allowed.includes(saved)) this.set(g, saved, false);
      });
      this.addEventListener('change', (e) => {
        const r = e.target;
        if (!r.matches('input[type=radio]')) return;
        this.set(r.name.startsWith('cols-m') ? 'm' : 'd', r.value, true);
      });
    }
    set(g, v, store) {
      this.root.dataset[g === 'd' ? 'colsD' : 'colsM'] = v;
      const r = this.querySelector(`input[name^="cols-${g}"][value="${v}"]`); if (r) r.checked = true;
      if (store) write(KEY[g], v);
      applySizes(this.root);
    }
  }
  if (!customElements.get('loupe-columns')) customElements.define('loupe-columns', LoupeColumns);

  /* ---------- grid: reveal after refresh, load more, infinite scroll ---------- */
  class LoupeCollectionGrid extends HTMLElement {
    connectedCallback() {
      this.root = this.closest('.mc__root');
      this.list = this.querySelector('[data-collection-list]');
      this.next = this.dataset.next || '';
      // cards re-rendered by the facets refresh are not watched by the page's reveal observer: show them now
      this.querySelectorAll('.reveal:not(.in)').forEach((el) => el.classList.add('in'));
      applySizes(this.root, this);
      if (this.dataset.mode === 'numbers' || !this.list || !this.next) return;

      this.btn = this.querySelector('[data-more-btn]');
      this.status = this.querySelector('[data-more-status]');
      this.bar = this.querySelector('[data-more-bar]');
      this.onClick = () => this.load(true);
      if (this.btn) this.btn.addEventListener('click', this.onClick);

      const sentinel = this.querySelector('[data-more-sentinel]');
      if (this.dataset.mode === 'infinite' && sentinel) {
        if ('IntersectionObserver' in window) {
          this.io = new IntersectionObserver((es) => { if (es.some((x) => x.isIntersecting)) this.load(false); }, { rootMargin: '0px 0px 800px 0px' });
          this.io.observe(sentinel);
          this.sentinel = sentinel;
        } else if (this.btn) this.btn.hidden = false;   // no observer: the button is the fallback
      }
    }

    disconnectedCallback() {
      if (this.io) this.io.disconnect();
      if (this.ctrl) this.ctrl.abort();
      if (this.btn && this.onClick) this.btn.removeEventListener('click', this.onClick);
    }

    async load(fromButton) {
      if (this.busy || !this.next) return;
      this.busy = true;
      this.ctrl = new AbortController();
      if (this.btn) { this.btn.classList.add('is-loading'); this.btn.setAttribute('aria-disabled', 'true'); }
      this.list.setAttribute('aria-busy', 'true');
      const pageUrl = new URL(this.next, location.origin);
      pageUrl.searchParams.delete('section_id');
      try {
        const u = new URL(pageUrl); u.searchParams.set('section_id', this.dataset.section);
        const r = await fetch(u, { signal: this.ctrl.signal });
        if (!r.ok) throw new Error(r.status);
        const doc = new DOMParser().parseFromString(await r.text(), 'text/html');
        if (!this.isConnected) return;   // the facets form refreshed the grid meanwhile
        const freshList = doc.querySelector('[data-collection-list]');
        const items = freshList ? [...freshList.children].filter((li) => !li.classList.contains('mc-promo')) : [];
        items.forEach((li) => li.classList.add('in'));
        this.list.append(...items);
        applySizes(this.root, this);

        const fresh = doc.querySelector('loupe-collection-grid');
        this.next = (fresh && fresh.dataset.next) || '';
        const fs = doc.querySelector('[data-more-status]'), fb = doc.querySelector('[data-more-bar]');
        if (fs && this.status) this.status.textContent = fs.textContent;
        if (fb && this.bar) this.bar.style.width = fb.style.width;
        history.replaceState(history.state, '', pageUrl.pathname + pageUrl.search + location.hash);

        if (fromButton && items[0]) {
          const f = items[0].querySelector('.card__title a, a[href]:not([tabindex="-1"])');
          if (f) f.focus({ preventScroll: true });
        }
        if (!this.next) this.done();
        else if (this.io && this.sentinel) { this.io.unobserve(this.sentinel); this.io.observe(this.sentinel); }   // re-check if still in view
      } catch (e) {
        if (e.name === 'AbortError') return;
        if (this.status) this.status.textContent = this.dataset.error || '';
        if (this.io) this.io.disconnect();
        if (this.btn) this.btn.hidden = false;   // fall back to the button after a failed auto-load
      } finally {
        this.busy = false;
        if (this.isConnected) {
          this.list.removeAttribute('aria-busy');
          if (this.btn) { this.btn.classList.remove('is-loading'); this.btn.removeAttribute('aria-disabled'); }
        }
      }
    }

    done() {
      if (this.io) this.io.disconnect();
      if (this.btn) {
        if (this.btn === document.activeElement && this.status) { this.status.tabIndex = -1; this.status.focus({ preventScroll: true }); }
        this.btn.hidden = true;
      }
    }
  }
  if (!customElements.get('loupe-collection-grid')) customElements.define('loupe-collection-grid', LoupeCollectionGrid);
})();
